import { cva, type VariantProps } from 'class-variance-authority';
import type { Route } from 'next';
import type { LinkProps } from 'next/link';
import Link from 'next/link';
import type * as React from 'react';
import { cn } from '@/utils/ui-utils';

const buttonLinkVariants = cva(
  'inline-flex items-center cursor-pointer justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/70',
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        outline: 'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-8 rounded-md px-3',
        lg: 'h-12 rounded-md px-8',
        icon: 'h-8 w-8',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonLink
  extends React.AnchorHTMLAttributes<HTMLAnchorElement>,
    Omit<LinkProps<Route>, 'href'>,
    VariantProps<typeof buttonLinkVariants> {
  href: Route;
  asChild?: boolean;
  className?: string;
  ariaLabel?: string;
  externalLink?: boolean;
}

const ButtonLink = ({
  href,
  className,
  ariaLabel,
  variant,
  size,
  externalLink = false,
  asChild = false,
  ...props
}: ButtonLink & React.ComponentProps<'a'>) => {
  if (externalLink) {
    return (
      <a
        href={href}
        target='_blank'
        className={cn(buttonLinkVariants({ variant, size, className }))}
        {...props}
        aria-label={ariaLabel}
      />
    );
  } else {
    return (
      <Link
        href={href}
        className={cn(buttonLinkVariants({ variant, size, className }))}
        {...props}
        data-nextlink
        aria-label={ariaLabel}
      />
    );
  }
};

// ButtonLink.displayName = 'ButtonLink';

export { ButtonLink, buttonLinkVariants };
